import React, { useEffect, useMemo, useRef, useState } from 'react';
import useTranslations from '@hooks/useTranslations';
import { IFeature, IPriceV2Types } from 'src/services';
import { getTotalPriceObjByType } from '@utils/Price.utils';
import { FEATURES_LIST } from 'src/context/featureSwitch';
import { useFeatureSwitchEnabled } from '@hooks/useFeatureSwitchEnabled';
import { useMoneyFormatter } from '@hooks/useMoneyFormatter';
import useAccessoryMonthlyPrices from '@hooks/useAccessoryPrices';
import PriceSuffix from '@components/PriceSuffix';
import Button from '@components/Button';
import dynamic from 'next/dynamic';
import { ScrollSyncPaneProps, ScrollSyncProps } from 'react-scroll-sync';
import { ITechnicalSpecificationsData } from '@utils/Characteristics.utils';
import { useGetNotEscapedTranslationMarkup } from '@hooks/useGetNotEscapedTranslationMarkup';
import {
    CategoryTitle,
    LegalMentions,
    Row,
    RowCell,
    RowTitle,
    Separator,
    Title,
    Wrapper,
} from './ModalEngineCompareStyled';
import { isBrandOV, isMarketGB } from '../../constants/main';

interface IModalEngineCompare {
    motorizations: IFeature[];
    onSubmit: (motorization: IFeature) => void;
}

interface IEngineComparatorRowTitleObject {
    key: string;
    label: string;
    start?: number;
    end?: number;
}

const { ScrollSync, ScrollSyncPane } = {
    ScrollSync: dynamic<ScrollSyncProps>(() => import('react-scroll-sync').then((mod) => mod.ScrollSync), {
        ssr: false,
    }),
    ScrollSyncPane: dynamic<ScrollSyncPaneProps>(() => import('react-scroll-sync').then((mod) => mod.ScrollSyncPane), {
        ssr: false,
    }),
};

const NAMES_ROW_ID = 'engineNameRowId';
const BUTTONS_ROW_ID = 'engineButtonRowId';

export const ModalEngineCompare: React.FC<IModalEngineCompare> = ({ motorizations, onSubmit }) => {
    const { t, hasValue } = useTranslations();
    const { getNotEscapedTranslationMarkup } = useGetNotEscapedTranslationMarkup();
    const { formatMoney } = useMoneyFormatter();
    const { getOptionMonthlyPrice } = useAccessoryMonthlyPrices();

    const [engineNameRowHeight, setEngineNameRowHeight] = useState(74);
    const [buttonsRowShadow, setButtonsRowShadow] = useState(false);

    const namesRef = useRef(null);
    const buttonsRef = useRef(null);

    const isSOLEnabled = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_SOL_ENABLED);
    const count = motorizations.length;

    const getTechnicalSpecifications = (motorization: IFeature, key: string) => {
        const { technicalSpecifications } = motorization;
        const { data } = technicalSpecifications.find((spec) => spec.key === key) || {};
        return data;
    };

    const getTechnicalSpecificationsAll = (key: string) => {
        return motorizations.map((motorization) => getTechnicalSpecifications(motorization, key));
    };

    const getCategoryTitle = (key: string) => {
        return motorizations?.[0].technicalSpecifications.find((spec) => spec.key === key)?.category ?? '';
    };

    const getStartEnd = (specs: ITechnicalSpecificationsData[], label: string) => {
        const localSpecs = [...specs];
        return {
            start: localSpecs.findIndex((spec) => spec?.label === label) + 1,
            end: localSpecs.length - localSpecs.reverse().findIndex((spec) => spec?.label === label),
        };
    };

    const checkCategoryRowsNotEmpty = (firstKey: string, secondKey: string | string[]): boolean => {
        const localSecondKey = typeof secondKey === 'string' ? [secondKey] : secondKey;
        const firstKeyData = getTechnicalSpecificationsAll(firstKey);
        return Boolean(
            firstKeyData?.find((motorizationFirstKey) =>
                motorizationFirstKey?.find(
                    (spec: ITechnicalSpecificationsData) => localSecondKey.includes(spec?.key) && spec?.value
                )
            )
        );
    };

    const renderRow = (firstKey: string, secondKey: string | string[], dark?: boolean) => {
        const localSecondKey = typeof secondKey === 'string' ? [secondKey] : secondKey;
        const firstKeyData = getTechnicalSpecificationsAll(firstKey);

        const specs =
            firstKeyData?.map((motorization: ITechnicalSpecificationsData[]) =>
                motorization?.find((spec: ITechnicalSpecificationsData) => localSecondKey.includes(spec.key))
            ) || [];

        const allEmpty = !specs?.filter((spec: ITechnicalSpecificationsData) => spec?.value)?.length;
        if (allEmpty) {
            return null;
        }

        let label = '';
        let key = '';
        const titles: IEngineComparatorRowTitleObject[] = [];

        specs?.forEach((spec) => {
            if (spec?.label && spec.label !== label) {
                label = spec.label;
                key = spec.key;
                const { start, end } = getStartEnd(specs, label);
                if (!titles.find((other) => other.start === start)) {
                    titles.push({
                        key,
                        label,
                        start,
                        end,
                    });
                }
            }
        });

        return (
            <ScrollSyncPane key={`row-${firstKey}-${secondKey}`}>
                <Row items={count} hasTitle dark={dark}>
                    {titles.map(({ key, label, start, end }) => (
                        <RowTitle start={start} end={end} key={`title-${start}-${end}`}>
                            {hasValue(`technicalFeatures.environmentalDetails.consumption.${key}`)
                                ? <span dangerouslySetInnerHTML={{ __html: t(`technicalFeatures.environmentalDetails.consumption.${key}`) }} />
                                : label
                            }
                        </RowTitle>
                    ))}
                    {specs.map((spec, idx: number) => (
                        <RowCell key={idx + 1} idx={idx + 1}>
                            {spec?.value || t('configurator.engineComparator.notAvailable')}
                        </RowCell>
                    ))}
                </Row>
            </ScrollSyncPane>
        );
    };

    // Abomination warning!
    // react-scroll-sync has internal flaw using cloneNode on its children
    // That makes std use of refs impossible, since references are lost during cloning.
    // Therefore we're accessing targets via ids & queryselectors
    const getRowPointers = () => {
        const namesRow = document.querySelector(`#${NAMES_ROW_ID}`);
        const buttonsRow = document.querySelector(`#${BUTTONS_ROW_ID}`);

        // elements may not be ready yet
        if (!namesRow || !buttonsRow) {
            setTimeout(getRowPointers, 100);
            return;
        }

        namesRef.current = namesRow;
        buttonsRef.current = buttonsRow;

        // Let's give the browser some time to render nameRow correctly before reading it's height
        // it is probably just only a dev issue connected to disabled cache and/or jit compilation
        // as custom fonts and lack of styles are larger than unstyled fallback font
        setTimeout(() => setEngineNameRowHeight(namesRow.clientHeight - 10), 200);
    };

    let scrollDebounceTimeout: NodeJS.Timeout = null;
    const scrollHandlerDebounce = () => {
        clearTimeout(scrollDebounceTimeout);
        scrollDebounceTimeout = setTimeout(() => scrollHandler(), 20);
    };

    const scrollHandler = () => {
        const { x, y } = buttonsRef.current?.getBoundingClientRect();
        const target = document.elementFromPoint(x, y);
        const overlap = target == namesRef.current;
        setButtonsRowShadow(overlap);
    };

    useEffect(() => {
        getRowPointers();

        const scrollWrap = document.querySelector('.modalWindow__wrap');
        scrollWrap?.addEventListener('scroll', scrollHandlerDebounce, { passive: true });

        return () => scrollWrap?.removeEventListener('scroll', scrollHandlerDebounce);
    }, []);

    const MOTORIZATION_KEY = '0001';
    const PERFORMANCE_KEY = '0005';
    const WLTP_KEY = '9111';

    const categories = useMemo(
        () =>
            [
                {
                    key: WLTP_KEY,
                    title: getCategoryTitle(WLTP_KEY),
                    rows: [
                        'consumptionCombined',
                        'co2Combined',
                        ['cycleLow', 'electricRange'],
                        'cycleMid',
                        'cycleHigh',
                        'cycleEhigh',
                    ],
                },
                {
                    key: PERFORMANCE_KEY,
                    title: getCategoryTitle(PERFORMANCE_KEY),
                    rows: ['0001', '0002'],
                },
                {
                    key: MOTORIZATION_KEY,
                    title: getCategoryTitle(MOTORIZATION_KEY),
                    rows: ['0001', '0004', '0005', '0006'],
                },
                {
                    key: '0006',
                    title: t('configurator.engineComparator.categoryTitle.capacity'),
                    rows: ['0005'],
                },
            ].filter(({ title, key, rows }) => title && checkCategoryRowsNotEmpty(key, rows.flat(1))),
        [motorizations]
    );

    return (
        <Wrapper>
            <Title>{t('configurator.engineComparator.title')}</Title>
            <div className="gridWrap">
                <ScrollSync>
                    <div className="grid">
                        <ScrollSyncPane>
                            <Row items={count} sticky={0} id={NAMES_ROW_ID}>
                                {motorizations.map((m, idx) => {
                                    const enginePrice = getOptionMonthlyPrice(m);

                                    return (
                                        <RowCell head key={idx}>
                                            <div className="engine-title">{m.title}</div>
                                            <div className="engine-price">
                                                {formatMoney(
                                                    isSOLEnabled
                                                        ? getTotalPriceObjByType(
                                                              m.pricesV2,
                                                              IPriceV2Types.TOTAL_B2C_CASH
                                                          )?.breakdown.finalCustomerCashPriceOnTheRoad
                                                        : getTotalPriceObjByType(
                                                              m.pricesV2,
                                                              IPriceV2Types.TOTAL_B2C_CASH
                                                          )?.finalPriceInclTax,
                                                    true
                                                )}{' '}
                                                {(enginePrice.price || enginePrice.defaultPrice) && (
                                                    <span>
                                                        <span className="or">
                                                            {t('configurator.optionPrice.suffix')}
                                                        </span>{' '}
                                                        {formatMoney(
                                                            enginePrice.price || enginePrice.defaultPrice,
                                                            true
                                                        )}{' '}
                                                        <PriceSuffix isMonthlyPrice hideAsterisk />
                                                    </span>
                                                )}
                                            </div>
                                        </RowCell>
                                    );
                                })}
                            </Row>
                        </ScrollSyncPane>
                        <ScrollSyncPane>
                            <Row items={count} dark>
                                {motorizations.map((m, idx) => (
                                    <RowCell gearbox key={idx}>
                                        {m.fuel}
                                        <br />
                                        {m.gearboxTitle}
                                    </RowCell>
                                ))}
                            </Row>
                        </ScrollSyncPane>
                        {renderRow(WLTP_KEY, 'consumptionCombined')}
                        {renderRow(WLTP_KEY, 'co2Combined', true)}
                        {renderRow(MOTORIZATION_KEY, '0004')}
                        <ScrollSyncPane>
                            <Row
                                items={count}
                                sticky={engineNameRowHeight}
                                shadow={buttonsRowShadow}
                                id={BUTTONS_ROW_ID}
                            >
                                {motorizations.map((m, idx) => (
                                    <RowCell key={idx}>
                                        <Button primary={isBrandOV && !isMarketGB} secondary={isBrandOV && isMarketGB} outline={!isBrandOV} onClick={() => onSubmit(m)}>
                                            {t('configurator.select')}
                                        </Button>
                                    </RowCell>
                                ))}
                            </Row>
                        </ScrollSyncPane>
                        <Separator />
                        {categories.map(({ key, title, rows }, categoryIndex) => (
                            <React.Fragment key={key}>
                                <CategoryTitle>{title}</CategoryTitle>
                                {rows.map((secondKey, index) => renderRow(key, secondKey, index % 2 === 0))}
                                {categoryIndex !== categories.length - 1 && <Separator />}
                            </React.Fragment>
                        ))}
                    </div>
                </ScrollSync>
            </div>
            <Separator large />
            <LegalMentions
                dangerouslySetInnerHTML={getNotEscapedTranslationMarkup('configurator.engineComparator.legalText')}
            />
        </Wrapper>
    );
};
